import { BiDashboardFilters } from '@euler/model/bi/dashboard';
import {
  BiRelativeTimePreset,
  BiTimeRange,
  BiTimeRangeKind,
  biRollingTimeRangeOfText,
} from '@euler/model/bi/query';

/** The ranges the filter bar offers, in the order it shows them. */
export const kBiDateRangeOptions: Array<{
  preset: BiRelativeTimePreset;
  label: string;
}> = [
  { preset: BiRelativeTimePreset.Today, label: '今天' },
  { preset: BiRelativeTimePreset.Last7Days, label: '近7天' },
  { preset: BiRelativeTimePreset.Last30Days, label: '近30天' },
  { preset: BiRelativeTimePreset.ThisMonth, label: '本月' },
  { preset: BiRelativeTimePreset.LastMonth, label: '上月' },
  { preset: BiRelativeTimePreset.Last90Days, label: '近90天' },
  { preset: BiRelativeTimePreset.ThisYear, label: '今年' },
  { preset: BiRelativeTimePreset.Last12Months, label: '近12个月' },
];

export function biSameTimeRange(
  a: BiTimeRange | undefined,
  b: BiTimeRange | undefined,
): boolean {
  if (!a || !b) return a === b;
  if (a.kind === BiTimeRangeKind.Relative)
    return b.kind === BiTimeRangeKind.Relative && a.preset === b.preset;
  if (a.kind === BiTimeRangeKind.Rolling)
    return (
      b.kind === BiTimeRangeKind.Rolling &&
      a.unit === b.unit &&
      a.count === b.count &&
      a.includeCurrent === b.includeCurrent
    );
  return (
    b.kind === BiTimeRangeKind.Absolute && a.from === b.from && a.to === b.to
  );
}

/**
 * The viewer's own range for this visit, over the dashboard's saved one.
 * `saved` sends no filters, so the server applies what is stored.
 */
export type BiDateChoice =
  | { kind: 'saved' }
  | { kind: 'override'; range: BiTimeRange | undefined };

export function biEffectiveRange(
  choice: BiDateChoice,
  saved: BiDashboardFilters,
): BiTimeRange | undefined {
  return choice.kind === 'saved' ? saved.dateRange : choice.range;
}

/** The body filters of a card data request under a choice. */
export function biRequestFilters(
  choice: BiDateChoice,
): BiDashboardFilters | undefined {
  if (choice.kind === 'saved') return undefined;
  return choice.range ? { dateRange: choice.range } : {};
}

/** The choice that each card's own range applies, as a route param. */
const kCardsOwnParam = 'cards';
const kAbsoluteParam = /^(\d{4}-\d{2}-\d{2})~(\d{4}-\d{2}-\d{2})$/;

/**
 * A date choice as a route param (the web URL's `range`), so it survives
 * going back from a card and reloading the page: a preset reads as itself
 * (`this_year`), a rolling range as `rolling:month:3:current`, fixed dates
 * as `2026-01-01~2026-03-31`, each card's own range as `cards`. The saved
 * range needs no param.
 */
export function biDateChoiceParam(choice: BiDateChoice): string | undefined {
  if (choice.kind === 'saved') return undefined;
  const { range } = choice;
  if (!range) return kCardsOwnParam;
  switch (range.kind) {
    case BiTimeRangeKind.Relative:
      return range.preset;
    case BiTimeRangeKind.Rolling:
      return (
        `rolling:${range.unit}:${range.count}:` +
        (range.includeCurrent ? 'current' : 'whole')
      );
    case BiTimeRangeKind.Absolute:
      return `${range.from}~${range.to}`;
  }
}

/** A route param back as a choice; anything unreadable is the saved one. */
export function biDateChoiceOfParam(param: string | undefined): BiDateChoice {
  if (!param) return { kind: 'saved' };
  if (param === kCardsOwnParam) return { kind: 'override', range: undefined };
  const preset = Object.values(BiRelativeTimePreset).find(
    value => value === param,
  );
  if (preset)
    return {
      kind: 'override',
      range: { kind: BiTimeRangeKind.Relative, preset },
    };
  const rolling = biRollingTimeRangeOfText(param);
  if (rolling) return { kind: 'override', range: rolling };
  const absolute = kAbsoluteParam.exec(param);
  if (absolute && absolute[1] <= absolute[2])
    return {
      kind: 'override',
      range: {
        kind: BiTimeRangeKind.Absolute,
        from: absolute[1],
        to: absolute[2],
      },
    };
  return { kind: 'saved' };
}
